// covered-by: frontends/carrie/src/ask-v2/review/__tests__/useAskV2ReviewVersionCompare.test.ts
import { type AskPunttMessage } from "@mg/schemas/src/christo/askPuntt";
import {
  type AskPunttReview,
  type AskPunttReviewComment,
  type AskPunttReviewResolved,
} from "@mg/schemas/src/christo/askPunttReview";
import { useEffect, useMemo, useState } from "react";

import { useAskPunttReview } from "./useAskPunttReview";
import { changePin, comparisonBetween, regionAt } from "./versionChanges";
import { type ReviewVersionOption } from "./VersionPicker";

import { useIsFlagOn } from "../../config/featureFlags";

type CompareSide = "before" | "after";

/**
 * The side-by-side version comparison of the review workspace: which review is
 * shown beside the open one, the comparison warhol made of the pair, the
 * change stepped to, and the pins and numbers both viewers draw.
 */
export function useAskV2ReviewVersionCompare({
  review,
  versionOptions,
  selectedCommentId,
  historyMessages,
  panelComments,
  commentNumbers,
}: {
  review: AskPunttReview | undefined;
  versionOptions: readonly ReviewVersionOption[];
  selectedCommentId: string | null;
  historyMessages: readonly Pick<
    AskPunttMessage,
    "comparisons" | "viewReviewId"
  >[];
  panelComments: readonly AskPunttReviewComment[];
  commentNumbers: Readonly<Record<string, number>>;
}) {
  const compareEnabled = useIsFlagOn("ask-puntt-version-compare");
  // The comment drawer shows the open comment's changed window before any
  // comparison opens.
  const drawerOn = useIsFlagOn("ask-v2-review-comment-drawer");
  const [comparing, setComparing] = useState(false);
  // Whether the two viewers pan, zoom, rotate and turn pages as one. On each
  // time a comparison opens.
  const [syncPanes, setSyncPanes] = useState(true);
  const [compareChoice, setCompareChoice] = useState<string | null>(null);
  const compareOptions = useMemo(
    () =>
      versionOptions.filter((option) => option.reviewId !== review?.reviewId),
    [versionOptions, review?.reviewId],
  );
  // The version just before this one, which is what a newer version is read
  // against; a choice the person made stands until this review becomes it.
  const compareReviewId = useMemo(() => {
    if (compareOptions.some((option) => option.reviewId === compareChoice)) {
      return compareChoice;
    }
    const here = versionOptions.findIndex(
      (option) => option.reviewId === review?.reviewId,
    );
    return (
      versionOptions[here - 1]?.reviewId ?? compareOptions[0]?.reviewId ?? null
    );
  }, [compareChoice, compareOptions, versionOptions, review?.reviewId]);
  const canCompare = compareEnabled && compareReviewId != null;
  const isComparing = canCompare && comparing;
  const selectedComment = useMemo(
    () =>
      review?.comments.find((comment) => comment._id === selectedCommentId) ??
      null,
    [review?.comments, selectedCommentId],
  );

  // why: the drawer's before and after needs the earlier version's file name,
  // so the pair is read while a comment is open there, not only once compared.
  const previewing =
    canCompare && drawerOn && selectedCommentId != null && !isComparing;
  // What the chat already knows changed between the two versions on screen:
  // the comparison warhol made of this pair, when it made one.
  const comparedQuery = useAskPunttReview(compareReviewId, {
    enabled: isComparing || previewing,
  });
  const comparedReview = comparedQuery.data?.review;
  const comparedFile = comparedReview?.files[0]?.name;
  const openFile = review?.files[0]?.name;
  // why version numbers: two versions uploaded under one name match either
  // way round by name, so which is the newer comes from the lineage instead.
  const sameName = openFile != null && openFile === comparedFile;
  const openIsNewer =
    review?.versionNumber != null && comparedReview?.versionNumber != null
      ? review.versionNumber > comparedReview.versionNumber
      : null;
  const newerReviewId =
    openIsNewer == null
      ? undefined
      : openIsNewer
        ? review?.reviewId
        : comparedReview?.reviewId;
  const pairComparison = useMemo(
    () =>
      // Stryker disable next-line ConditionalExpression: pairComparison is only read through comparison (null unless isComparing) and commentRegion (requires previewing), so computing it when both are false changes no result
      isComparing || previewing
        ? comparisonBetween(
            historyMessages,
            openFile,
            comparedFile,
            newerReviewId,
          )
        : null,
    [
      comparedFile,
      isComparing,
      previewing,
      openFile,
      historyMessages,
      newerReviewId,
    ],
  );
  const comparison = isComparing ? pairComparison : null;
  const changedRegions = comparison?.regions;
  // Which side of the pair the open review is: its change pins are rings on
  // the older version and solid on the newer one.
  const pairSide: CompareSide | null = !pairComparison
    ? null
    : sameName
      ? openIsNewer
        ? "after"
        : "before"
      : pairComparison.before === openFile
        ? "before"
        : // Stryker disable next-line ConditionalExpression: comparisonBetween only returns a comparison of this pair, so when the before check above is false the after check is always true and `true` gives the same "after"; the `false` replacement, which would drop the after side, is killed by the differently-named after-file test
          pairComparison.after === openFile
          ? "after"
          : null;
  const openSide = comparison ? pairSide : null;
  const comparedSide: CompareSide | null =
    openSide === "before" ? "after" : openSide === "after" ? "before" : null;
  // why: the earlier version always sits on the left, as a reader reads a
  // change, whichever of the two is the open review. Version numbers decide,
  // then the comparison's own before/after, then the options' order (oldest
  // first).
  const openIsEarlier =
    openIsNewer != null
      ? !openIsNewer
      : openSide != null
        ? openSide === "before"
        : versionOptions.findIndex(
            (option) => option.reviewId === review?.reviewId,
          ) <
          versionOptions.findIndex(
            (option) => option.reviewId === compareReviewId,
          );
  // Marks of "reviewed" live on the newer version's review of the pair.
  const newerReview = !comparison
    ? undefined
    : openSide === "after"
      ? review
      : comparedReview;
  const reviewedNumbers = useMemo(
    () =>
      new Set(
        (newerReview?.reviewedChanges ?? [])
          .filter((mark) => mark.comparisonId === comparison?.id)
          .map((mark) => mark.number),
      ),
    [comparison?.id, newerReview?.reviewedChanges],
  );
  // The change stepped to, by its place in the list; a finding picked in the
  // column, another pair or a closed comparison ends the walk.
  const [changeAt, setChangeAt] = useState<number | null>(null);
  useEffect(() => {
    setChangeAt(null);
  }, [selectedCommentId, compareReviewId, review?.reviewId, isComparing]);
  const steppedRegion =
    changeAt != null ? (changedRegions?.[changeAt] ?? null) : null;
  const steppedPin = useMemo(
    () => (steppedRegion ? changePin(steppedRegion) : null),
    [steppedRegion],
  );
  // why only with the newer version open: the windows are boxed on the newer
  // version's pages, and a finding of the older one sits on its own.
  const canPlaceFinding = openSide === "after";
  const selectedRegion = useMemo(
    () =>
      canPlaceFinding && changedRegions && selectedComment
        ? regionAt(changedRegions, selectedComment)
        : null,
    [canPlaceFinding, changedRegions, selectedComment],
  );
  // A fixed finding's "View change" opens the comparison with the earlier
  // version; its window is stepped to once the comparison is on screen,
  // because opening it resets the step above.
  const [windowToShow, setWindowToShow] = useState<number | null>(null);
  const comparedLoaded =
    comparedQuery.data?.review.reviewId === compareReviewId;
  useEffect(() => {
    if (windowToShow == null) return;
    if (!changedRegions) {
      // why: a pair the chat never compared has no window to step to; kept,
      // the number would step a later comparison of some other pair.
      if (comparedLoaded) setWindowToShow(null);
      return;
    }
    const at = changedRegions.findIndex(
      (region) => region.number === windowToShow,
    );
    if (at >= 0) setChangeAt(at);
    setWindowToShow(null);
  }, [changedRegions, comparedLoaded, windowToShow]);
  // why: a comparison shows only its changed windows, on both artworks and in
  // the left column; the findings stay in the review.
  const showChanges = comparison != null;
  const changePins = useMemo(
    () => (changedRegions ?? []).map(changePin),
    [changedRegions],
  );
  const changeNumbers = useMemo(() => {
    const numbers: Record<string, number> = {};
    (changedRegions ?? []).forEach((region, index) => {
      numbers[changePins[index]._id] = region.number;
    });
    return numbers;
  }, [changePins, changedRegions]);
  const comparedChanges = useMemo(
    () =>
      showChanges ? { pins: changePins, numbers: changeNumbers } : undefined,
    [changeNumbers, changePins, showChanges],
  );
  const viewerComments = useMemo(() => {
    if (showChanges) {
      // The finding picked in the column keeps its pin among the windows.
      return selectedComment && !steppedPin
        ? [...changePins, selectedComment]
        : changePins;
    }
    return steppedPin ? [...panelComments, steppedPin] : panelComments;
  }, [changePins, panelComments, selectedComment, showChanges, steppedPin]);
  const viewerNumbers = useMemo(
    () =>
      showChanges || steppedPin
        ? { ...commentNumbers, ...changeNumbers }
        : commentNumbers,
    [changeNumbers, commentNumbers, showChanges, steppedPin],
  );
  const stepChange = (by: 1 | -1) => {
    const total = changedRegions?.length ?? 0;
    if (total === 0) return;
    setChangeAt((current) =>
      current == null
        ? by === 1
          ? 0
          : total - 1
        : (current + by + total) % total,
    );
  };
  /** Steps to the change a pin stands for; false when it is not a change. */
  const stepToPin = (comment: { _id: string }) => {
    const at = changePins.findIndex((pin) => pin._id === comment._id);
    if (at >= 0) setChangeAt(at);
    return at >= 0;
  };
  const openComparison = (on: boolean) => {
    setComparing(on);
    if (on) setSyncPanes(true);
    if (!on) setWindowToShow(null);
  };
  /** "View change" on a fixed finding: the pair with its earlier version, at its window. */
  const viewResolvedChange = (entry: AskPunttReviewResolved) => {
    setCompareChoice(entry.priorReviewId);
    setComparing(true);
    setSyncPanes(true);
    setWindowToShow(entry.windows[0] ?? null);
  };
  // why the version number first: an in-flight version is left out of the
  // options, and counting places would then shift every later mark.
  const markOf = (reviewId: string | null | undefined) => {
    const at = versionOptions.findIndex(
      (option) => option.reviewId === reviewId,
    );
    if (at < 0) return undefined;
    return `V${versionOptions[at].versionNumber ?? at + 1}`;
  };
  const versionMark = (reviewId: string | null | undefined) =>
    isComparing ? markOf(reviewId) : undefined;
  const markOr = (reviewId: string | null | undefined, fallback: string) =>
    markOf(reviewId) ?? fallback;
  // The open comment's changed window, for the drawer: only with the newer
  // version open, for the reason `canPlaceFinding` gives.
  const commentRegion = useMemo(
    () =>
      previewing && pairSide === "after" && pairComparison && selectedComment
        ? regionAt(pairComparison.regions, selectedComment)
        : null,
    [pairComparison, pairSide, previewing, selectedComment],
  );
  const commentChange = commentRegion
    ? {
        region: commentRegion,
        beforeMark: markOr(compareReviewId, "V1"),
        // why: a version still in flight is left out of the options.
        afterMark: markOr(review?.reviewId, "V2"),
      }
    : null;
  /** "Show comparison" in the drawer: the pair opened at the comment's window. */
  const showChangeAt = (number: number) => {
    setComparing(true);
    setSyncPanes(true);
    setWindowToShow(number);
  };
  // The mark each side of the comparison is shown under ("V1", "V2"): the
  // review on screen that is that side. why openSide, not the file name: two
  // versions under one name would both read as the open one.
  const compareSideMark = (side: CompareSide) => {
    const reviewId = side === openSide ? review?.reviewId : compareReviewId;
    return versionMark(reviewId) ?? (side === "before" ? "V1" : "V2");
  };

  return {
    canCompare,
    isComparing,
    setComparing: openComparison,
    syncPanes,
    setSyncPanes,
    openIsEarlier,
    compareReviewId,
    compareOptions,
    setCompareChoice,
    comparedQuery,
    comparison,
    changedRegions,
    openSide,
    comparedSide,
    newerReview,
    reviewedNumbers,
    changeAt,
    setChangeAt,
    steppedRegion,
    steppedPin,
    selectedComment,
    selectedRegion,
    canPlaceFinding,
    showChanges,
    comparedChanges,
    viewerComments,
    viewerNumbers,
    stepChange,
    stepToPin,
    viewResolvedChange,
    commentChange,
    showChangeAt,
    versionMark,
    compareSideMark,
  };
}
